import React, { useState, useEffect } from 'react';
import { 
  KeyIcon, 
  DocumentTextIcon, 
  CheckCircleIcon, 
  ClockIcon,
  ShieldCheckIcon,
  ServerIcon,
  CpuChipIcon,
  ArrowRightIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/apiClient';
import { extractLicenses, extractRequests, extractActivationKeys, extractClusterLicenses, extractFromSettled } from '../utils/apiHelpers';
import toast from 'react-hot-toast';

const CustomerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalLicenses: 0,
    activeLicenses: 0,
    totalRequests: 0,
    pendingRequests: 0,
    approvedRequests: 0,
    activationKeys: 0,
    pendingActivationKeys: 0,
    approvedActivationKeys: 0,
    clusterLicenses: 0,
    pendingClusterLicenses: 0,
    approvedClusterLicenses: 0,
    totalHosts: 0,
    totalCores: 0
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        
        // Check if user is authenticated
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('[Customer Dashboard] No authentication token found');
          setLoading(false);
          return;
        }
        
        const [licensesRes, requestsRes, activationKeysRes, clusterLicensesRes] = await Promise.allSettled([
          apiClient.get('/licenses'),
          apiClient.get('/license-requests/mine'),
          apiClient.get('/activation-keys/my-keys'),
          apiClient.get('/cluster-licenses/my-licenses')
        ]);

        // Process licenses using standardized helpers
        const licenses = extractFromSettled(licensesRes, extractLicenses);
        const activeLicenses = licenses.filter(l => l.status === 'ACTIVE');

        // Process license requests
        const requests = extractFromSettled(requestsRes, extractRequests);
        const pendingRequests = requests.filter(r => r.status === 'PENDING');
        const approvedRequests = requests.filter(r => r.status === 'APPROVED');

        // Process activation keys
        const activationKeys = extractFromSettled(activationKeysRes, extractActivationKeys);
        const pendingActivationKeys = activationKeys.filter(k => k.status === 'PENDING');
        const approvedActivationKeys = activationKeys.filter(k => k.status === 'APPROVED');

        // Process cluster licenses
        const clusterLicenses = extractFromSettled(clusterLicensesRes, extractClusterLicenses);
        const pendingClusterLicenses = clusterLicenses.filter(c => c.status === 'PENDING');
        const approvedClusterLicenses = clusterLicenses.filter(c => c.status === 'APPROVED');

        // Calculate cluster stats
        let totalHosts = 0;
        let totalCores = 0;
        
        approvedClusterLicenses.forEach(cluster => {
          totalHosts += cluster.totalHosts || 0;
          totalCores += cluster.totalCores || 0;
        });

        setStats({
          totalLicenses: licenses.length,
          activeLicenses: activeLicenses.length,
          totalRequests: requests.length,
          pendingRequests: pendingRequests.length,
          approvedRequests: approvedRequests.length,
          activationKeys: activationKeys.length,
          pendingActivationKeys: pendingActivationKeys.length,
          approvedActivationKeys: approvedActivationKeys.length,
          clusterLicenses: clusterLicenses.length,
          pendingClusterLicenses: pendingClusterLicenses.length,
          approvedClusterLicenses: approvedClusterLicenses.length,
          totalHosts,
          totalCores
        });

        // Build recent activity
        const activity = [];
        
        approvedClusterLicenses.slice(0, 2).forEach(cluster => {
          activity.push({
            id: `cluster-${cluster.id}`,
            message: `Cluster license approved - ${cluster.hostname} (${cluster.totalHosts} hosts)`,
            timestamp: new Date(cluster.approvedAt || cluster.updatedAt).toLocaleString(),
            icon: ServerIcon,
            color: 'text-blue-600'
          });
        });

        approvedActivationKeys.slice(0, 2).forEach(key => {
          activity.push({
            id: `activation-${key.id}`,
            message: `Activation key approved - ${key.hostname || 'System'}`,
            timestamp: new Date(key.approvedAt || key.updatedAt).toLocaleString(),
            icon: ShieldCheckIcon,
            color: 'text-purple-600'
          });
        });

        approvedRequests.slice(0, 2).forEach(request => {
          activity.push({
            id: `request-${request.id}`,
            message: `License request approved - ${request.clusterName || 'License'}`,
            timestamp: new Date(request.approvedAt || request.updatedAt).toLocaleString(),
            icon: CheckCircleIcon,
            color: 'text-green-600'
          });
        });

        activity.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setRecentActivity(activity.slice(0, 6));

      } catch (error) {
        console.error('Error loading dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const statCards = [
    {
      name: 'Active Licenses',
      value: stats.activeLicenses,
      subtitle: `${stats.totalLicenses} total`,
      icon: CheckCircleIcon,
      color: 'bg-green-500',
      onClick: () => navigate('/dashboard/my-licenses')
    },
    {
      name: 'Pending Requests',
      value: stats.pendingRequests + stats.pendingActivationKeys + stats.pendingClusterLicenses,
      subtitle: 'Awaiting approval',
      icon: ClockIcon,
      color: 'bg-yellow-500',
      onClick: () => navigate('/dashboard/my-requests')
    },
    {
      name: 'Activation Keys',
      value: stats.approvedActivationKeys,
      subtitle: `${stats.pendingActivationKeys} pending`,
      icon: ShieldCheckIcon,
      color: 'bg-purple-500',
      onClick: () => navigate('/dashboard/activation-keys')
    },
    {
      name: 'Cluster Licenses',
      value: stats.approvedClusterLicenses,
      subtitle: `${stats.totalHosts} hosts, ${stats.totalCores} cores`,
      icon: ServerIcon,
      color: 'bg-blue-500',
      onClick: () => navigate('/dashboard/cluster-licenses')
    }
  ];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Welcome back, {user?.firstName}!</h1>
        <p className="text-gray-600 mt-1">Here's an overview of your licenses and requests.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <div
            key={stat.name}
            onClick={stat.onClick}
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                <p className="text-xs text-gray-500 mt-1">{stat.subtitle}</p>
              </div>
              <div className={`p-3 rounded-lg ${stat.color} group-hover:scale-110 transition-transform`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
            </div>
            <div className="p-6">
              {recentActivity.length === 0 ? (
                <div className="text-center py-12">
                  <ExclamationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">No recent activity</p>
                  <p className="text-xs text-gray-400 mt-1">Your approved licenses and requests will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="p-2 rounded-lg bg-gray-100">
                        <activity.icon className={`h-4 w-4 ${activity.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 font-medium">{activity.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          {/* Quick Actions Card */}
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            </div>
            <div className="p-6 space-y-3">
              {console.log('🔥 CustomerDashboard Quick Actions Rendering - BUILD VERSION 2024-12-12-FINAL')}
              <button
                onClick={() => {
                  console.log('🔥 Upload Activation Key clicked - navigating to /dashboard/activation-keys');
                  navigate('/dashboard/activation-keys');
                }}
                className="w-full flex items-center justify-between p-3 text-left rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center">
                  <ShieldCheckIcon className="h-5 w-5 text-gray-600 mr-3 group-hover:text-[#197560]" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Upload Activation Key</div>
                    <div className="text-xs text-gray-500">Upload new activation key file</div>
                  </div>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-400 group-hover:text-[#197560]" />
              </button>
              
              <button
                onClick={() => {
                  console.log('🔥 Request Cluster License clicked - navigating to /dashboard/cluster-licenses');
                  navigate('/dashboard/cluster-licenses');
                }}
                className="w-full flex items-center justify-between p-3 text-left rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center">
                  <DocumentTextIcon className="h-5 w-5 text-gray-600 mr-3 group-hover:text-[#197560]" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Request Cluster License</div>
                    <div className="text-xs text-gray-500">Submit a new cluster license request</div>
                  </div>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-400 group-hover:text-[#197560]" />
              </button>
              
              <button
                onClick={() => navigate('/dashboard/licenses')}
                className="w-full flex items-center justify-between p-3 text-left rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center">
                  <KeyIcon className="h-5 w-5 text-gray-600 mr-3 group-hover:text-[#197560]" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">View All Licenses</div>
                    <div className="text-xs text-gray-500">Browse active licenses</div>
                  </div>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-400 group-hover:text-[#197560]" />
              </button>
              
              <button
                onClick={() => navigate('/dashboard/my-requests')}
                className="w-full flex items-center justify-between p-3 text-left rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-gray-600 mr-3 group-hover:text-[#197560]" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Track Requests</div>
                    <div className="text-xs text-gray-500">Check request status</div>
                  </div>
                </div>
                <ArrowRightIcon className="h-4 w-4 text-gray-400 group-hover:text-[#197560]" />
              </button>
            </div>
          </div>

          {/* Pending Items */}
          {(stats.pendingRequests > 0 || stats.pendingActivationKeys > 0 || stats.pendingClusterLicenses > 0) && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl shadow-sm">
              <div className="p-6 border-b border-amber-200">
                <h3 className="text-lg font-semibold text-amber-900">Pending Items</h3>
              </div>
              <div className="p-6 space-y-3">
                {stats.pendingRequests > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-800">License Requests</span>
                    <span className="flex items-center text-sm font-semibold text-amber-900">
                      {stats.pendingRequests}
                      <ClockIcon className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                )}
                {stats.pendingActivationKeys > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-800">Activation Keys</span>
                    <span className="flex items-center text-sm font-semibold text-amber-900">
                      {stats.pendingActivationKeys}
                      <ClockIcon className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                )}
                {stats.pendingClusterLicenses > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-800">Cluster Licenses</span>
                    <span className="flex items-center text-sm font-semibold text-amber-900">
                      {stats.pendingClusterLicenses}
                      <ClockIcon className="w-4 h-4 ml-1" />
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;